/*! Copyright (c) Safe As Milk. All rights reserved. */
import { debounce } from "utils";

import ItemsScroll from "items-scroll";

class ItemsScrollPagedNavigation extends ItemsScroll {
    #boundItemsScroll;
    #boundHandleStateChange;
    #boundPageNavigationClick;
    #boundPointerDown;
    #boundPointerMove;
    #boundPointerUp;
    #controlsListenersEnabled=false;
    #isDragging=false;
    #dragPointerId=null;
    #pointerStartX=0;
    #hasDragged=false;
    #activePageIndex=0;
    #itemAtScrollIndex;
    #navigationPagesAmount;
    #resizeObserver;
    #ticking=false;
    constructor() {
        super();
        this.pageNavigationWrapper = null;
        this.pageNavigationTrack = null;
        this.paginationStyle = this.getAttribute("pagination-style") === "dots" ? "dots" : "progress";
        this.navigationCounterOnly = this.hasAttribute("mobile-counter-only");
        this.navigationCounterSeparator = "/";
        this.navigationCounterShowForPagesAmount = this.hasAttribute("dots-only") ? 0 : 7;
        this.navigationCounterShowUpToContainerSize = 768;
        this.#boundItemsScroll = this.#handleItemsScroll.bind(this);
        this.#boundHandleStateChange = this.#handleStateChange.bind(this);
        this.#boundPageNavigationClick = this.#handlePageNavigationClick.bind(this);
        this.#boundPointerDown = this.#handlePointerDown.bind(this);
        this.#boundPointerMove = this.#handlePointerMove.bind(this);
        this.#boundPointerUp = this.#handlePointerUp.bind(this);
    }
    static #DRAG_THRESHOLD = 3;
    get #usesProgressTrack() {
        return this.paginationStyle !== "dots";
    }
    connectedCallback() {
        super.connectedCallback();
        this.#setPageNavigationState();
        this.#resizeObserver = new ResizeObserver(debounce((() => {
            this.#updatePageNavigationList();
        })), 50);
        this.#resizeObserver.observe(this.items);
        this.addEventListener("on:items-scroll:change-state", this.#boundHandleStateChange);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener("on:items-scroll:change-state", this.#boundHandleStateChange);
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = null;
        }
        this.#disableControlsListeners();
    }
    #enableControlsListeners() {
        if (this.#controlsListenersEnabled) return;
        this.items.addEventListener("scroll", this.#boundItemsScroll);
        if (this.#usesProgressTrack) {
            if (this.pageNavigationTrack) {
                this.pageNavigationTrack.addEventListener("pointerdown", this.#boundPointerDown);
                this.pageNavigationTrack.addEventListener("pointermove", this.#boundPointerMove);
                this.pageNavigationTrack.addEventListener("pointerup", this.#boundPointerUp);
                this.pageNavigationTrack.addEventListener("pointercancel", this.#boundPointerUp);
            }
        } else if (this.pageNavigationContainer) {
            this.pageNavigationContainer.addEventListener("click", this.#boundPageNavigationClick);
        }
        this.#controlsListenersEnabled = true;
    }
    #disableControlsListeners() {
        if (!this.#controlsListenersEnabled) return;
        this.items.removeEventListener("scroll", this.#boundItemsScroll);
        if (this.pageNavigationTrack) {
            this.pageNavigationTrack.removeEventListener("pointerdown", this.#boundPointerDown);
            this.pageNavigationTrack.removeEventListener("pointermove", this.#boundPointerMove);
            this.pageNavigationTrack.removeEventListener("pointerup", this.#boundPointerUp);
            this.pageNavigationTrack.removeEventListener("pointercancel", this.#boundPointerUp);
        }
        if (this.pageNavigationContainer) {
            this.pageNavigationContainer.removeEventListener("click", this.#boundPageNavigationClick);
        }
        this.#controlsListenersEnabled = false;
    }
    #getCurrentNavigationPageIndex() {
        const itemAtScrollIndex = this.#itemAtScrollIndex ?? 0;
        if (itemAtScrollIndex + this.itemsPerPage > this.items.children.length - 1) {
            return this.#navigationPagesAmount - 1;
        }
        return Math.floor(itemAtScrollIndex / this.itemsPerPage);
    }
    #ensurePageNavigationContainer() {
        const needsTrack = this.#usesProgressTrack;
        const isTrack = this.pageNavigationContainer?.classList.contains("page-navigation--track");
        if (this.pageNavigationContainer && isTrack !== needsTrack) {
            this.#disableControlsListeners();
            this.pageNavigationContainer.remove();
            this.pageNavigationContainer = null;
            this.pageNavigationTrack = null;
        }
        if (this.pageNavigationContainer) return;
        if (needsTrack) {
            this.pageNavigationContainer = document.createElement("div");
            this.pageNavigationContainer.classList.add("page-navigation", "page-navigation--track", "media-carousel__pagination--track");
            this.pageNavigationContainer.innerHTML = `\n            <span class="media-carousel__pagination-track">\n              <span class="media-carousel__pagination-thumb"></span>\n            </span>\n          `;
            this.pageNavigationWrapper.appendChild(this.pageNavigationContainer);
            this.pageNavigationTrack = this.pageNavigationContainer.querySelector(".media-carousel__pagination-track");
        } else {
            this.pageNavigationContainer = document.createElement("ul");
            this.pageNavigationContainer.classList.add("page-navigation");
            this.pageNavigationContainer.setAttribute("role", "tablist");
            this.pageNavigationWrapper.appendChild(this.pageNavigationContainer);
        }
    }
    #renderDots(pagesAmount) {
        this.pageNavigationContainer.innerHTML = `\n            ${Array.from({
            length: pagesAmount
        }, ((_, i) => `\n              <li class="page-navigation__item" role="presentation">\n                <button type="button" role="tab" class="page-navigation__button" data-page="${i + 1}" aria-label="Slide ${i + 1} of ${this.#navigationPagesAmount}" tabindex="0" aria-selected="false">\n                  <span class="visually-hidden">${i + 1}</span>\n                </button>\n              </li>\n            `)).join("\n")}\n          `;
    }
    #updatePageNavigationList() {
        if (this.items && this.items.children && this.items.children.length > 0) {
            const children = Array.from(this.items.children);
            const total = children.length;
            children.forEach((child, index) => {
                if (child.tagName === "A" || child.classList.contains("collection-list__link")) {
                    return;
                }
                child.setAttribute("role", "group");
                child.setAttribute("aria-roledescription", "slide");
                child.setAttribute("aria-label", `${index + 1} of ${total}`);
            });
        }
        if (!this.pageNavigationWrapper) {
            this.pageNavigationWrapper = document.createElement("div");
            this.pageNavigationWrapper.classList.add("page-navigation-wrapper");
            if (this.#usesProgressTrack) {
                this.pageNavigationWrapper.setAttribute("tabindex", "-1");
                this.pageNavigationWrapper.setAttribute("aria-hidden", "true");
            }
            this.appendChild(this.pageNavigationWrapper);
        }
        const pagesAmount = Math.ceil(this.items.children.length / this.itemsPerPage);
        if (pagesAmount !== this.#navigationPagesAmount) {
            this.#navigationPagesAmount = pagesAmount;
            if (this.navigationCounterShowUpToContainerSize && this.navigationCounterShowUpToContainerSize >= this.offsetWidth && this.navigationCounterShowForPagesAmount && (this.navigationCounterOnly || this.#navigationPagesAmount >= this.navigationCounterShowForPagesAmount)) {
                if (this.pageNavigationContainer) this.pageNavigationContainer.setAttribute("hidden", true);
                if (!this.pageNavigationCounter) {
                    this.pageNavigationCounter = document.createElement("div");
                    this.pageNavigationCounter.classList.add("page-navigation-counter");
                    this.pageNavigationCounter.setAttribute("role", "status");
                    this.pageNavigationWrapper.appendChild(this.pageNavigationCounter);
                }
                this.pageNavigationCounter.removeAttribute("hidden");
                this.pageNavigationCounter.innerHTML = `\n          <span class="page-navigation-counter__current">${this.#itemAtScrollIndex || 1}</span>\n          <span class="page-navigation-counter__separator">${this.navigationCounterSeparator}</span>\n          <span class="page-navigation-counter__total">${this.#navigationPagesAmount}</span>\n        `;
                this.#setPageNavigationState();
            } else {
                if (this.pageNavigationCounter) this.pageNavigationCounter.setAttribute("hidden", true);
                this.#ensurePageNavigationContainer();
                this.pageNavigationContainer.removeAttribute("hidden");
                if (this.#navigationPagesAmount > 1) {
                    if (this.#usesProgressTrack) {
                        this.pageNavigationContainer.style.setProperty("--slide-count", this.#navigationPagesAmount);
                    } else {
                        this.#renderDots(pagesAmount);
                    }
                    this.#setPageNavigationState();
                } else if (this.#usesProgressTrack) {
                    this.pageNavigationContainer.setAttribute("hidden", true);
                } else {
                    this.pageNavigationContainer.innerHTML = "";
                }
            }
        }
    }

    #focusFirstVisibleItem(targetPageIndex) {
        const itemsPerPage = this.itemsPerPage || 1;
        const pageIndex = typeof targetPageIndex === "number" && targetPageIndex >= 0 ? targetPageIndex : 0;
        this.updateItemInertState(pageIndex, true);
        const startIndex = pageIndex * itemsPerPage;
        const firstItem = this.items.children[startIndex];
        if (!firstItem) return;
        setTimeout(() => {
            const focusable = firstItem.querySelector('a:not([tabindex="-1"]), button:not([disabled]), [tabindex]:not([tabindex="-1"])') || firstItem.querySelector('a, button') || firstItem;
            if (focusable && typeof focusable.focus === "function") {
                focusable.focus({ preventScroll: true });
            }
        }, 50);
    }
    #handleStateChange({detail: {scrollable: scrollable}}) {
        if (scrollable) {
            this.#updatePageNavigationList();
            this.#enableControlsListeners();
        } else {
            this.updateItemInertState(0, false);
            if (this.pageNavigationContainer) {
                this.#disableControlsListeners();
            }
        }
    }
    #handlePageNavigationClick(e) {
        const {target: target} = e;
        if (!target.classList.contains("page-navigation__button")) return;
        const page = Number(target.dataset.page);
        const targetPageIndex = page - 1;
        this.#navigateToPage(targetPageIndex, "smooth");
    }
    #navigateToPage(targetPageIndex, behavior = "smooth") {
        const targetChild = this.items.children[this.itemsPerPage * targetPageIndex];
        if (!targetChild) return;
        const targetOffset = targetChild.offsetLeft;
        this.updateItemInertState(targetPageIndex, true);
        const movePromise = this.#moveItems(targetOffset, behavior);
        if (behavior === "smooth" && movePromise && typeof movePromise.then === "function") {
            movePromise.then(() => {
                this.#focusFirstVisibleItem(targetPageIndex);
            });
        }
    }
    #getRatioFromPointer(clientX) {
        const rect = this.pageNavigationTrack.getBoundingClientRect();
        return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    }
    #getPageIndexFromRatio(ratio) {
        if (this.#navigationPagesAmount <= 1) return 0;
        return Math.min(Math.round(ratio * (this.#navigationPagesAmount - 1)), this.#navigationPagesAmount - 1);
    }
    #setThumbRatio(ratio) {
        if (!this.pageNavigationContainer) return;
        const fractionalIndex = this.#navigationPagesAmount > 1 ? ratio * (this.#navigationPagesAmount - 1) : 0;
        this.pageNavigationContainer.style.setProperty("--active-index", fractionalIndex);
    }
    #dragToRatio(ratio) {
        this.#setThumbRatio(ratio);
        const maxScroll = this.items.scrollWidth - this.items.offsetWidth;
        if (maxScroll <= 0) return;
        this.items.scrollLeft = ratio * maxScroll;
    }
    #handlePointerDown(event) {
        if (!this.pageNavigationTrack || !this.#navigationPagesAmount || event.button !== 0 && event.pointerType === "mouse") return;
        this.#isDragging = true;
        this.#dragPointerId = event.pointerId;
        this.#pointerStartX = event.clientX;
        this.#hasDragged = false;
        this.pageNavigationContainer?.classList.add("is-dragging");
        this.pageNavigationTrack.setPointerCapture(event.pointerId);
        event.preventDefault();
    }
    #handlePointerMove(event) {
        if (!this.#isDragging || event.pointerId !== this.#dragPointerId) return;
        if (Math.abs(event.clientX - this.#pointerStartX) >= ItemsScrollPagedNavigation.#DRAG_THRESHOLD) {
            this.#hasDragged = true;
        }
        if (!this.#hasDragged) return;
        this.#dragToRatio(this.#getRatioFromPointer(event.clientX));
        event.preventDefault();
    }
    #handlePointerUp(event) {
        if (!this.#isDragging || event.pointerId !== this.#dragPointerId) return;
        if (this.pageNavigationTrack.hasPointerCapture(event.pointerId)) {
            this.pageNavigationTrack.releasePointerCapture(event.pointerId);
        }
        const ratio = this.#getRatioFromPointer(event.clientX);
        const pageIndex = this.#getPageIndexFromRatio(ratio);
        const didDrag = this.#hasDragged;
        this.#hasDragged = false;
        this.#isDragging = false;
        this.pageNavigationContainer?.classList.remove("is-dragging");
        this.#dragPointerId = null;
        if (didDrag || pageIndex !== this.#activePageIndex) {
            this.#navigateToPage(pageIndex, didDrag ? "instant" : "smooth");
        } else {
            this.#updatePageNavigationVisualState(this.#activePageIndex);
        }
        event.preventDefault();
    }
    #updatePageNavigationVisualState(currentNavigationPageIndex) {
        this.#activePageIndex = currentNavigationPageIndex;
        if (this.pageNavigationContainer && !this.pageNavigationContainer.hasAttribute("hidden")) {
            if (this.#usesProgressTrack) {
                this.pageNavigationContainer.style.setProperty("--slide-count", this.#navigationPagesAmount);
                this.pageNavigationContainer.style.setProperty("--active-index", currentNavigationPageIndex);
            } else {
                this.pageNavigationContainer.querySelectorAll("button").forEach(((button, index) => {
                    if (index === currentNavigationPageIndex) {
                        button.classList.add("is-active");
                        button.setAttribute("aria-selected", "true");
                    } else {
                        button.classList.remove("is-active");
                        button.setAttribute("aria-selected", "false");
                    }
                }));
            }
        }
        if (this.pageNavigationCounter) {
            const pageNavigationCounterCurrentElement = this.pageNavigationCounter.querySelector(".page-navigation-counter__current");
            this.pageNavigationCounter.setAttribute("aria-label", `Page ${currentNavigationPageIndex + 1} of ${this.#navigationPagesAmount}`);
            if (pageNavigationCounterCurrentElement) {
                pageNavigationCounterCurrentElement.innerHTML = currentNavigationPageIndex + 1;
            }
        }
    }
    #setPageNavigationState() {
        if (this.pageNavigationDisabled || this.#isDragging) return;
        if (!this.hasAttribute("scrollable")) {
            this.updateItemInertState(0, false);
            return;
        }
        if (!this.#navigationPagesAmount || this.#navigationPagesAmount <= 1) {
            this.updateItemInertState(0, false);
            return;
        }
        const {itemAtScroll: itemAtScroll} = this;
        const itemAtScrollIndex = [ ...this.items.children ].indexOf(itemAtScroll);
        this.#itemAtScrollIndex = itemAtScrollIndex;
        const currentNavigationPageIndex = this.#getCurrentNavigationPageIndex();
        this.updateItemInertState(currentNavigationPageIndex, true);
        this.#updatePageNavigationVisualState(currentNavigationPageIndex);
    }
    #handleItemsScroll() {
        if (this.pageNavigationDisabled) return;
        if (!this.#ticking) {
            window.requestAnimationFrame((() => {
                this.#setPageNavigationState();
                this.#ticking = false;
            }));
            this.#ticking = true;
        }
    }
    #moveItems(position, behavior = "instant") {
        let itemsScrollX = position;
        if (itemsScrollX < 0) {
            itemsScrollX = 0;
        } else if (itemsScrollX > this.items.scrollWidth - this.items.offsetWidth) {
            itemsScrollX = this.items.scrollWidth - this.items.offsetWidth;
        }
        if (behavior === "instant") {
            this.items.scrollTo({
                behavior: "instant",
                left: itemsScrollX
            });
            return Promise.resolve(itemsScrollX);
        } else {
            return this.smoothScrollItems(itemsScrollX);
        }
    }
}

customElements.define("items-scroll-paged-navigation", ItemsScrollPagedNavigation);
//# sourceMappingURL=items-scroll-paged-navigation.js.map
